import { ChatContext, initialChatContext } from "@/Contexts/ChatContext";
import { ChatPageProps } from "@/Pages/Chat/Chat";
import { usePage } from "@/utils/turboSafeInertia";
import { useDebounce } from "@/utils/useDebounce";
import { PropsWithChildren, useEffect, useRef, useState } from "react";
import {
    StreamChat as ChatClient,
    ConnectionOpen,
    ErrorFromResponse,
} from "stream-chat";
import { Chat as StreamChat } from "stream-chat-react";
import { preserveCustomAttachmentsOnEdit } from "./preserveCustomAttachmentsOnEdit";

export type Unread = {
    [channelId: string]: {
        count: number;
    };
};

const FROZEN_CLASSIFICATION_BATCH_SIZE = 30;
const UNREAD_REFRESH_DEBOUNCE_MS = 2000;
const UNREAD_REFRESH_MAX_WAIT_MS = 10000;

const STREAM_RATE_LIMIT_ERROR_CODE = 9;

const isStreamRateLimitError = (error: unknown) =>
    error instanceof ErrorFromResponse &&
    error.code === STREAM_RATE_LIMIT_ERROR_CODE;

export function ChatProvider({ children }: PropsWithChildren) {
    const { chat, user, twoFactorPassed } = usePage<ChatPageProps>().props;

    const chatClient = ChatClient.getInstance(
        import.meta.env.VITE_GET_STREAM_APP_ID,
        { timeout: 10000 },
    );

    const connection = useRef<ConnectionOpen>(null);
    const connectionAttempt = useRef<ReturnType<
        typeof chatClient.connectUser
    > | null>(null);
    const frozenCids = useRef<Set<string>>(new Set());
    const activeCids = useRef<Set<string>>(new Set());

    const [isReady, setIsReady] = useState(false);
    const [unread, setUnread] = useState<Unread>(initialChatContext.unread);

    const toCid = (channelId: string) =>
        channelId.includes(":") ? channelId : `messaging:${channelId}`;

    const toChannelKey = (channelId: string) =>
        channelId.includes(":")
            ? channelId.slice(channelId.indexOf(":") + 1)
            : channelId;

    /**
     * Frozen channels (ended engagements, unengaged proactive matches) can hold
     * unread counts from server-sent messages but should not surface badges.
     * The unread counts endpoint doesn't expose frozen, so the few channels
     * with unread are classified with one targeted queryChannels call, cached
     * for the session. This replaced paginating through every channel with
     * queryChannels, which was burning Stream's per-minute API query budget.
     */
    const classifyCidsAsFrozenOrActive = async (cids: string[]) => {
        const unknown = cids.filter(
            (cid) =>
                !frozenCids.current.has(cid) && !activeCids.current.has(cid),
        );
        for (
            let i = 0;
            i < unknown.length;
            i += FROZEN_CLASSIFICATION_BATCH_SIZE
        ) {
            const batch = unknown.slice(
                i,
                i + FROZEN_CLASSIFICATION_BATCH_SIZE,
            );
            const notFrozen = await chatClient.queryChannels(
                { cid: { $in: batch }, frozen: false },
                {},
                { limit: FROZEN_CLASSIFICATION_BATCH_SIZE },
            );
            notFrozen.forEach((channel) => activeCids.current.add(channel.cid));
            batch
                .filter((cid) => !activeCids.current.has(cid))
                .forEach((cid) => frozenCids.current.add(cid));
        }
    };

    /**
     * Stream rate-limit errors (code 9) are expected under bursty chat
     * activity and are swallowed: the badge just refreshes on the next event.
     */
    const refreshUnread = async () => {
        try {
            const response = await chatClient.getUnreadCount();
            const unreadChannels = response.channels.filter(
                (channel) => channel.unread_count > 0,
            );
            await classifyCidsAsFrozenOrActive(
                unreadChannels.map((channel) => toCid(channel.channel_id)),
            );
            const newUnread: Unread = {};
            unreadChannels.forEach((channel) => {
                if (frozenCids.current.has(toCid(channel.channel_id))) {
                    return;
                }
                newUnread[toChannelKey(channel.channel_id)] = {
                    count: channel.unread_count,
                };
            });
            setUnread(newUnread);
        } catch (error) {
            if (!isStreamRateLimitError(error)) {
                throw error;
            }
        }
    };

    const debouncedRefreshUnread = useDebounce(
        UNREAD_REFRESH_DEBOUNCE_MS,
        refreshUnread,
        UNREAD_REFRESH_MAX_WAIT_MS,
    );

    // Stream throws "connectUser was called twice" if a second connect starts before the first
    // resolves, which leaves the chat stuck loading until the page is refreshed. The in-flight
    // attempt is held in a ref because connection.current is only set once the promise settles.
    const connectUser = async () => {
        if (!chat || !user || connectionAttempt.current) return;

        const attempt = chatClient.connectUser(
            {
                id: chat.userChatId,
                name: `${user.first_name} ${user.last_name ?? ""}`,
                image: user.profile_photo_url,
            },
            chat.token,
        );
        connectionAttempt.current = attempt;

        try {
            connection.current = (await attempt) ?? null;
            setIsReady(true);
        } catch (error) {
            connectionAttempt.current = null;
            throw error;
        }
    };

    const disconnectUser = async () => {
        setIsReady(false);
        connection.current = null;
        connectionAttempt.current = null;
        await chatClient.disconnectUser();
    };

    useEffect(() => {
        chatClient.setMessageComposerSetupFunction(
            preserveCustomAttachmentsOnEdit,
        );
    }, [chatClient]);

    useEffect(() => {
        if (user && twoFactorPassed && !connection.current) {
            connectUser();
        } else if ((!user || !twoFactorPassed) && connection.current) {
            disconnectUser();
        }
    }, [user?.type, user?.id, twoFactorPassed]);

    useEffect(() => {
        if (!chat || !chatClient || !isReady) return;
        refreshUnread();
        const subscriptions = [
            "notification.message_new",
            "notification.mark_read",
            "notification.mark_unread",
            "message.new",
            "message.read",
        ].map((eventType) =>
            chatClient.on(eventType, () => debouncedRefreshUnread()),
        );
        return () => {
            subscriptions.forEach((subscription) => subscription.unsubscribe());
            debouncedRefreshUnread.cancel();
        };
    }, [chatClient, isReady]);

    return (
        <ChatContext.Provider
            value={{
                isReady,
                unread,
            }}
        >
            <StreamChat client={chatClient}>{children}</StreamChat>
        </ChatContext.Provider>
    );
}
